import type { ReactNode } from 'react';
import { Button } from './Button.js';

/**
 * A tela inteira falhou — na prática, a API não respondeu e o roteador não
 * consegue nem decidir se há sessão.
 *
 * Existia como nada: o roteador avisava no console que nenhuma rota tinha
 * `errorComponent` e a pessoa via uma página em branco. Página em branco é a
 * única tela que não diz o que está acontecendo. Depois existiu duas vezes, uma
 * por SPA, com a mesma anatomia e frases diferentes — o portal fala com o
 * associado, a interna com a operação. A anatomia mora aqui; a frase é de quem
 * chama.
 *
 * O detalhe técnico fica disponível sem virar a manchete da tela.
 */
export function FailureNotice({
  title,
  error,
  children,
  action = 'Tentar de novo',
  onAction = () => window.location.reload(),
}: {
  title: string;
  error?: Error;
  children: ReactNode;
  action?: string;
  onAction?: () => void;
}) {
  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <div className="w-full max-w-md">
        <h1 className="titulo-tela">{title}</h1>
        <p className="mt-2 text-sm text-texto-suave">{children}</p>
        {error && (
          <details className="mt-4">
            <summary className="cursor-pointer text-xs font-medium text-texto-suave">
              Detalhe técnico
            </summary>
            <p className="mt-1.5 rounded-pequeno bg-superficie-suave p-2 font-mono text-xs text-texto-suave">
              {error.message}
            </p>
          </details>
        )}
        <Button className="mt-5" onClick={onAction}>
          {action}
        </Button>
      </div>
    </div>
  );
}

/**
 * A ficha pediu um registro que não existe — o `404` da API, e não uma falha.
 *
 * Classe própria, e não uma frase comparada: a tela precisa separar "não
 * existe" (o caminho é outro registro, ou outra base) de "não carregou" (o
 * caminho é tentar de novo), e comparar `error.message` com uma frase quebra no
 * dia em que alguém melhora a frase.
 */
export class NotFoundError extends Error {
  override readonly name = 'NotFoundError';
}

export function isNotFound(error: unknown): error is NotFoundError {
  return error instanceof NotFoundError;
}
